// ============================================
// SOUND EFFECT 2026 - Import paths
// ============================================
import handClapMp3 from '@/assets/audio/sfx-2026/hand-clap.mp3'
import drumMp3 from '@/assets/audio/sfx-2026/drumroll.mp3'
import confettiMp3 from '@/assets/audio/sfx-2026/confetti.mp3'
import spinnerTickMp3 from '@/assets/audio/spinner-wheel-tick.mp3'
import spinnerCelebrationMp3 from '@/assets/audio/spinner-wheel-celebration.mp3'
import countdown5Mp3 from '@/assets/audio/sfx-2026/countdown-to-show-question.mp3'
import countdown3Mp3 from '@/assets/audio/sfx-2026/countdown-to-end-question.mp3'
import startQuizMp3 from '@/assets/audio/sfx-2026/start-quiz.mp3'
import endNowMp3 from '@/assets/audio/sfx-2026/end-now-button.mp3'
import showAnswerMp3 from '@/assets/audio/show-answer.mp3'
import leaderboardMiddleMp3 from '@/assets/audio/middle-leaderboard-music.mp3'
import leaderboardFinalMp3 from '@/assets/audio/final-leaderboard-music.mp3'

// Music sources
import lobbyMp3 from '@/assets/audio/lobby-music2.mp3'
import playingQuizMp3 from '@/assets/audio/playing-quiz-music.mp3'

// ============================================
// SOUND EFFECT SOURCES - Only paths, lazy load
// ============================================
const SOUND_EFFECT_SOURCES = {
  HAND_CLAP: handClapMp3,
  DRUM: drumMp3,
  CONFETTI: confettiMp3,
  SPINNER_TICK: spinnerTickMp3,
  SPINNER_CELEBRATION: spinnerCelebrationMp3,
  COUNTDOWN_5: countdown5Mp3,
  COUNTDOWN_3: countdown3Mp3,
  START_QUIZ: startQuizMp3,
  END_NOW: endNowMp3,
  SHOW_ANSWER: showAnswerMp3,
  LEADERBOARD_MIDDLE: leaderboardMiddleMp3,
  LEADERBOARD_FINAL: leaderboardFinalMp3,
}

// ============================================
// MUSIC SOURCES - Only paths, lazy load
// ============================================
const MUSIC_SOURCES = {
  LOBBY: lobbyMp3,
  PLAYING_QUIZ: playingQuizMp3,
}

// ============================================
// CACHE - Store created Audio instances
// ============================================
const soundEffectCache = {}
const musicCache = {}

// ============================================
// FACTORY FUNCTIONS - Lazy load with cache
// ============================================

/**
 * Get sound effect by key - lazy load and cache
 * @param {string} key - Sound effect key (e.g., 'CONFETTI', 'HAND_CLAP')
 * @returns {Audio|null} - Audio instance or null if key not found
 */
export const getSoundEffect = (key) => {
  if (!soundEffectCache[key]) {
    const src = SOUND_EFFECT_SOURCES[key]
    if (!src) return null
    soundEffectCache[key] = new Audio(src)
  }
  return soundEffectCache[key]
}

/**
 * Get music by key - lazy load and cache
 * @param {string} key - Music key (e.g., 'LOBBY', 'PLAYING_QUIZ')
 * @returns {Audio|null} - Audio instance or null if key not found
 */
export const getMusic = (key) => {
  if (!musicCache[key]) {
    const src = MUSIC_SOURCES[key]
    if (!src) return null
    musicCache[key] = new Audio(src)
  }
  return musicCache[key]
}

/**
 * Create dynamic music from URL (for background music, slide audio)
 * @param {string} url - Music URL
 * @param {Object} options - Options (loop, preload)
 * @returns {Audio} - New Audio instance
 */
export const createDynamicMusic = (url, options = {}) => {
  const audio = new Audio(url)
  if (options.loop) audio.loop = true
  if (options.preload) audio.preload = options.preload
  return audio
}

// Export keys for type safety
export const SOUND_EFFECT_KEYS = Object.keys(SOUND_EFFECT_SOURCES)
export const MUSIC_KEYS = Object.keys(MUSIC_SOURCES)

// ============================================
// HELPER FUNCTIONS
// ============================================
const pauseSound = (sound) => {
  sound.pause()
}

const playSound = (sound) => {
  sound.play().catch(() => {})
}

const stopSound = (sound) => {
  sound.pause()
  // eslint-disable-next-line no-param-reassign
  sound.currentTime = 0
}

// ============================================
// STATE
// ============================================
const _state = {
  activeSounds: [], // Active sound effects
  activeMusics: [], // Active music (static + dynamic)
}

// ============================================
// GETTERS
// ============================================
const _getters = {
  isEnableSoundEffects: (state, getters, rootState) => {
    // Fallback to legacy value for backward compatibility
    const sfxValue = rootState.presentation?.isEnableSoundEffects
    if (sfxValue !== undefined) return sfxValue
    return rootState.presentation?.isEnableQuizMusic ?? true
  },
  isEnableMusic: (state, getters, rootState) => {
    return rootState.presentation?.isEnableMusic ?? true
  },
}

// ============================================
// MUTATIONS
// ============================================
const mutations = {
  ADD_ACTIVE_SOUND(state, sound) {
    if (!state.activeSounds.includes(sound)) {
      state.activeSounds.push(sound)
    }
  },
  REMOVE_ACTIVE_SOUND(state, sound) {
    const index = state.activeSounds.indexOf(sound)
    if (index > -1) {
      state.activeSounds.splice(index, 1)
    }
  },
  ADD_ACTIVE_MUSIC(state, music) {
    if (!state.activeMusics.includes(music)) {
      state.activeMusics.push(music)
    }
  },
  REMOVE_ACTIVE_MUSIC(state, music) {
    const index = state.activeMusics.indexOf(music)
    if (index > -1) {
      state.activeMusics.splice(index, 1)
    }
  },
  CLEAR_ALL_ACTIVE_SOUNDS(state) {
    // eslint-disable-next-line no-param-reassign
    state.activeSounds = []
  },
  CLEAR_ALL_ACTIVE_MUSICS(state) {
    // eslint-disable-next-line no-param-reassign
    state.activeMusics = []
  },
}

// ============================================
// ACTIONS
// ============================================
const actions = {
  // ---- Sound Effects ----
  // Toggle: on = resume, off = pause
  toggleSoundEffectAction({
    state, dispatch, rootState, getters,
  }) {
    const newValue = !getters.isEnableSoundEffects

    // Mute/unmute active sounds (keep playing to stay in sync with animation)
    state.activeSounds.forEach((sound) => {
      // eslint-disable-next-line no-param-reassign
      sound.muted = !newValue
    })

    // Persist to API (update presentation)
    const presentationId = rootState.presentation?.id
    if (presentationId) {
      dispatch('updatePresentationByIdAction', {
        id: presentationId,
        payload: { isEnableSoundEffects: newValue },
      }, { root: true })
    }
  },

  /**
   * Play sound effect by key (lazy load)
   * @param {string} key - Sound effect key (e.g., 'CONFETTI')
   * @returns {Audio|null} - Audio instance or null
   */
  playSoundEffectAction({ state, getters, commit }, key) {
    const sound = getSoundEffect(key)
    if (!sound) return null

    // Skip if already active (playing or muted-playing)
    if (state.activeSounds.includes(sound)) return sound

    commit('ADD_ACTIVE_SOUND', sound)
    // Auto-remove from activeSounds when playback ends
    sound.onended = () => {
      commit('REMOVE_ACTIVE_SOUND', sound)
    }
    stopSound(sound) // Reset to start
    sound.muted = !getters.isEnableSoundEffects
    playSound(sound) // Always play to keep time in sync
    return sound
  },

  /**
   * Stop sound effect by key
   * @param {string} key - Sound effect key
   */
  stopSoundEffectAction({ commit }, key) {
    const sound = getSoundEffect(key)
    if (!sound) return

    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  /**
   * Play sound effect instance directly (for dynamic sounds)
   * @param {Audio} sound - Audio instance
   */
  playSoundInstanceAction({ state, getters, commit }, sound) {
    if (!sound) return
    if (state.activeSounds.includes(sound)) return

    commit('ADD_ACTIVE_SOUND', sound)
    sound.onended = () => {
      commit('REMOVE_ACTIVE_SOUND', sound)
    }
    stopSound(sound)
    // eslint-disable-next-line no-param-reassign
    sound.muted = !getters.isEnableSoundEffects
    playSound(sound)
  },

  /**
   * Stop sound effect instance directly
   * @param {Audio} sound - Audio instance
   */
  stopSoundInstanceAction({ commit }, sound) {
    if (!sound) return
    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  registerSoundAction({ commit }, sound) {
    commit('ADD_ACTIVE_SOUND', sound)
  },

  unregisterSoundAction({ commit }, sound) {
    stopSound(sound)
    commit('REMOVE_ACTIVE_SOUND', sound)
  },

  // ---- Music ----
  // Toggle: on = unmute, off = mute (keep playing to maintain time position)
  toggleMusicAction({
    state, dispatch, rootState, getters,
  }) {
    const newValue = !getters.isEnableMusic

    // Mute/unmute active musics (keep playing to stay in sync)
    state.activeMusics.forEach((music) => {
      // eslint-disable-next-line no-param-reassign
      music.muted = !newValue
    })

    // Persist to API (update presentation)
    const presentationId = rootState.presentation?.id
    if (presentationId) {
      dispatch('updatePresentationByIdAction', {
        id: presentationId,
        payload: { isEnableMusic: newValue },
      }, { root: true })
    }
  },

  /**
   * Play music by key (lazy load)
   * @param {string} key - Music key (e.g., 'LOBBY')
   * @returns {Audio|null} - Audio instance or null
   */
  playMusicAction({ state, getters, commit }, key) {
    const music = getMusic(key)
    if (!music) return null

    // Skip if already active (playing or muted-playing)
    if (state.activeMusics.includes(music)) return music

    commit('ADD_ACTIVE_MUSIC', music)
    music.muted = !getters.isEnableMusic
    playSound(music)
    return music
  },

  /**
   * Stop music by key
   * @param {string} key - Music key
   */
  stopMusicAction({ commit }, key) {
    const music = getMusic(key)
    if (!music) return

    stopSound(music)
    commit('REMOVE_ACTIVE_MUSIC', music)
  },

  /**
   * Pause music by key (do not reset currentTime)
   * @param {string} key - Music key
   */
  pauseMusicAction(_, key) {
    const music = getMusic(key)
    if (!music) return

    pauseSound(music)
  },

  // ---- Dynamic Music (background music, slide audio) ----
  /**
   * Register dynamic music element (from component ref)
   * Called when component mounts with music from URL
   * @param {HTMLAudioElement} music - Music element
   */
  registerDynamicMusicAction({ commit }, music) {
    if (!music) return
    commit('ADD_ACTIVE_MUSIC', music)
  },

  /**
   * Unregister dynamic music element
   * Called when component destroys
   * @param {HTMLAudioElement} music - Music element
   */
  unregisterDynamicMusicAction({ commit }, music) {
    if (!music) return
    commit('REMOVE_ACTIVE_MUSIC', music)
  },

  /**
   * Play dynamic music instance directly
   * @param {HTMLAudioElement} music - Music element
   */
  playDynamicMusicAction({ state, getters, commit }, music) {
    if (!music) return
    if (state.activeMusics.includes(music)) return

    commit('ADD_ACTIVE_MUSIC', music)
    // eslint-disable-next-line no-param-reassign
    music.muted = !getters.isEnableMusic
    playSound(music)
  },

  /**
   * Stop dynamic music instance
   * @param {HTMLAudioElement} music - Music element
   */
  stopDynamicMusicAction({ commit }, music) {
    if (!music) return
    stopSound(music)
    commit('REMOVE_ACTIVE_MUSIC', music)
  },

  /**
   * Pause dynamic music instance (do not reset currentTime)
   * @param {HTMLAudioElement} music - Music element
   */
  pauseDynamicMusicAction(_, music) {
    if (!music) return
    pauseSound(music)
  },

  // ---- Stop All (when exiting presenting mode) ----
  /**
   * Stop all active sounds and musics
   * Called when user exits presenting mode
   */
  stopAllAction({ state, commit }) {
    // Stop all sound effects
    state.activeSounds.forEach(stopSound)
    commit('CLEAR_ALL_ACTIVE_SOUNDS')

    // Stop all musics
    state.activeMusics.forEach(stopSound)
    commit('CLEAR_ALL_ACTIVE_MUSICS')
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  mutations,
  actions,
}
